import type { DisciplioSettings } from "./types";

export const MAX_WEEKLY_GOAL_CATEGORY_LENGTH = 80;
export const DEFAULT_WEEKLY_GOAL_CATEGORY_LIMIT = 10;
export const MAX_WEEKLY_GOAL_CATEGORY_LIMIT = 30;

const TITLE_KEYWORDS = [
  [
    "work",
    "job",
    "project",
    "client",
    "business",
    "ship",
    "build",
    "code",
    "praca",
    "projekt",
    "klient",
    "biznes",
    "робот",
    "проєкт",
    "клієнт",
    "бізнес",
  ],
  [
    "health",
    "sleep",
    "doctor",
    "therapy",
    "recovery",
    "nutrition",
    "zdrow",
    "sen",
    "lekarz",
    "terapi",
    "regener",
    "diet",
    "здоров",
    "сон",
    "лікар",
    "терап",
    "віднов",
    "харч",
  ],
  [
    "fitness",
    "workout",
    "gym",
    "run",
    "exercise",
    "training",
    "sport",
    "trening",
    "siłown",
    "bieg",
    "ćwicz",
    "спортзал",
    "біг",
    "вправ",
    "тренув",
  ],
  [
    "learn",
    "study",
    "read",
    "course",
    "book",
    "skill",
    "practice",
    "nauk",
    "ucz",
    "czyt",
    "kurs",
    "książ",
    "umiejęt",
    "навч",
    "чит",
    "курс",
    "книг",
    "навич",
  ],
  [
    "rest",
    "relax",
    "vacation",
    "hobby",
    "game",
    "recreation",
    "leisure",
    "odpocz",
    "relaks",
    "urlop",
    "hobby",
    "rekreac",
    "відпоч",
    "відпуст",
    "хобі",
    "дозвіл",
  ],
  [
    "finance",
    "save",
    "budget",
    "invest",
    "income",
    "money",
    "expense",
    "finans",
    "oszcz",
    "budżet",
    "inwest",
    "dochód",
    "pienią",
    "wydatk",
    "фінанс",
    "заощад",
    "бюджет",
    "інвест",
    "дохід",
    "грош",
    "витрат",
  ],
  [
    "personal",
    "organize",
    "routine",
    "home",
    "admin",
    "journal",
    "osobist",
    "organiz",
    "rutyn",
    "dom",
    "spraw",
    "dziennik",
    "особист",
    "організ",
    "рутин",
    "дім",
    "справ",
    "щоденник",
  ],
  [
    "relationship",
    "family",
    "partner",
    "friend",
    "social",
    "call",
    "relac",
    "rodzin",
    "partner",
    "przyjac",
    "spotk",
    "rozmow",
    "стосунк",
    "родин",
    "партнер",
    "друз",
    "зустр",
    "спілк",
  ],
] as const;

export function defaultWeeklyGoalCategoriesForLanguage(language: string): string[] {
  const normalized = language.toLowerCase();
  if (normalized.startsWith("pl")) {
    return ["Praca i projekty", "Zdrowie", "Fitness", "Nauka i rozwój", "Odpoczynek i rekreacja", "Finanse", "Osobiste", "Relacje"];
  }
  if (normalized.startsWith("uk")) {
    return ["Робота й проєкти", "Здоров’я", "Фітнес", "Навчання й розвиток", "Відпочинок і дозвілля", "Фінанси", "Особисте", "Стосунки"];
  }
  return ["Work & Projects", "Health", "Fitness", "Learning & Growth", "Rest & Recreation", "Finance", "Personal", "Relationships"];
}

export function normalizeWeeklyGoalCategories(values: unknown, max = MAX_WEEKLY_GOAL_CATEGORY_LIMIT): string[] {
  if (!Array.isArray(values)) return [];
  const seen = new Set<string>();
  const result: string[] = [];
  for (const value of values) {
    const category = typeof value === "string" ? value.trim().replace(/\s+/g, " ") : "";
    const key = category.toLocaleLowerCase();
    if (!category || category.length > MAX_WEEKLY_GOAL_CATEGORY_LENGTH || seen.has(key)) continue;
    seen.add(key);
    result.push(category);
    if (result.length >= max) break;
  }
  return result;
}

export function mergeWeeklyGoalCategories(defaults: string[], custom: string[]): string[] {
  return normalizeWeeklyGoalCategories([...defaults, ...custom], MAX_WEEKLY_GOAL_CATEGORY_LIMIT + defaults.length);
}

const TITLE_TOKEN_PATTERN = /[\p{L}\p{N}]+/gu;

function titleTokens(value: string): string[] {
  return value.toLocaleLowerCase().match(TITLE_TOKEN_PATTERN) ?? [];
}

function tokenMatchesWord(token: string, word: string): boolean {
  if (token === word) return true;
  // The keyword catalog contains both full words and intentional stems (for example
  // "zdrow" -> "zdrowie"). Allow a short suffix for those stems, but do not let
  // the four-letter "work" keyword match the unrelated word "workouts".
  return word.length >= 4 && token.startsWith(word) && token.length - word.length <= 3;
}

function containsTokenSequence(tokens: string[], sequence: string[]): boolean {
  if (!sequence.length || sequence.length > tokens.length) return false;
  return tokens.some((_, start) =>
    start + sequence.length <= tokens.length &&
    sequence.every((word, offset) => tokenMatchesWord(tokens[start + offset], word))
  );
}

function scoreCategory(category: string, index: number, title: string, defaults: string[], currentCategory: string): number {
  const normalizedCategory = category.toLocaleLowerCase();
  const normalizedCurrent = currentCategory.trim().toLocaleLowerCase();
  const normalizedTitleTokens = titleTokens(title);
  let score = 0;
  if (normalizedCurrent && normalizedCategory === normalizedCurrent) score += 10_000;
  const categoryTokens = titleTokens(normalizedCategory);
  if (containsTokenSequence(normalizedTitleTokens, categoryTokens)) score += 1_000;
  const defaultIndex = defaults.findIndex((entry) => entry.toLocaleLowerCase() === normalizedCategory);
  if (defaultIndex >= 0 && TITLE_KEYWORDS[defaultIndex].some((keyword) => normalizedTitleTokens.some((token) => tokenMatchesWord(token, keyword)))) score += 100;
  score += categoryTokens.filter((token) => normalizedTitleTokens.some((titleToken) => tokenMatchesWord(titleToken, token))).length * 20;
  return score - index / 1_000;
}

export function recommendedWeeklyGoalCategories(
  availableCategories: string[],
  title: string,
  language: string,
  currentCategory = "",
): string[] {
  const defaults = defaultWeeklyGoalCategoriesForLanguage(language);
  return normalizeWeeklyGoalCategories(availableCategories, MAX_WEEKLY_GOAL_CATEGORY_LIMIT + defaults.length)
    .map((category, index) => ({ category, score: scoreCategory(category, index, title, defaults, currentCategory), index }))
    .sort((left, right) => right.score - left.score || left.index - right.index)
    .map(({ category }) => category);
}

export function filterWeeklyGoalCategorySuggestions(suggestions: string[], value: string): string[] {
  const query = value.trim().toLocaleLowerCase();
  if (!query) return suggestions;
  return suggestions.filter((category) => category.toLocaleLowerCase().includes(query));
}

export function settingsWithWeeklyGoalCategories(settings: DisciplioSettings): DisciplioSettings {
  const defaultCategories = defaultWeeklyGoalCategoriesForLanguage(settings.language);
  const defaultKeys = new Set(defaultCategories.map((category) => category.toLocaleLowerCase()));
  const customCategories = normalizeWeeklyGoalCategories(settings.customWeeklyGoalCategories)
    .filter((category) => !defaultKeys.has(category.toLocaleLowerCase()));
  const limit = settings.customWeeklyGoalCategoryLimit > 0
    ? settings.customWeeklyGoalCategoryLimit
    : DEFAULT_WEEKLY_GOAL_CATEGORY_LIMIT;
  return {
    ...settings,
    defaultWeeklyGoalCategories: defaultCategories,
    customWeeklyGoalCategories: customCategories,
    weeklyGoalCategories: mergeWeeklyGoalCategories(defaultCategories, customCategories),
    customWeeklyGoalCategoryLimit: limit,
  };
}

export function appendCustomWeeklyGoalCategory(settings: DisciplioSettings, value: string): DisciplioSettings {
  const current = settingsWithWeeklyGoalCategories(settings);
  const normalized = value.trim().replace(/\s+/g, " ");
  if (!normalized || normalized.length > MAX_WEEKLY_GOAL_CATEGORY_LENGTH) return current;
  if (current.weeklyGoalCategories.some((category) => category.toLocaleLowerCase() === normalized.toLocaleLowerCase())) return current;
  if (current.customWeeklyGoalCategories.length >= current.customWeeklyGoalCategoryLimit) return current;
  return settingsWithWeeklyGoalCategories({
    ...current,
    customWeeklyGoalCategories: [...current.customWeeklyGoalCategories, normalized],
  });
}
